import {PureComponent} from "react";
import {MAP} from "react-google-maps/lib/constants";

export const MAP_DRAWING_MODES = {
    MARKER: "marker",
    POLYLINE: "polyline",
    POLYGON: "polygon",
    RECTANGLE: "rectangle",
    CIRCLE: "circle",
};

const LABELS = {
    [MAP_DRAWING_MODES.MARKER]: "Marker",
    [MAP_DRAWING_MODES.POLYLINE]: "Line",
    [MAP_DRAWING_MODES.POLYGON]: "Polygon",
    [MAP_DRAWING_MODES.RECTANGLE]: "Rectangle",
    [MAP_DRAWING_MODES.CIRCLE]: "Circle",
};

const ICONS = {
    pan: '<svg viewBox="0 0 24 24"><path d="M7 11.5V6.8a1.3 1.3 0 0 1 2.6 0v4.1"/><path d="M9.6 10.9V5.2a1.3 1.3 0 0 1 2.6 0v5.7"/><path d="M12.2 10.6V6.2a1.3 1.3 0 0 1 2.6 0v5"/><path d="M14.8 11.3V8.4a1.3 1.3 0 0 1 2.6 0v6.3c0 3.6-2.4 6.3-6.2 6.3h-.9c-2 0-3.4-.8-4.7-2.5L3.2 15a1.4 1.4 0 0 1 2.3-1.6L7 15.1"/></svg>',
    [MAP_DRAWING_MODES.MARKER]: '<svg viewBox="0 0 24 24"><path d="M12 22s7-7.2 7-12A7 7 0 0 0 5 10c0 4.8 7 12 7 12Z"/><circle cx="12" cy="10" r="2.4"/></svg>',
    [MAP_DRAWING_MODES.POLYLINE]: '<svg viewBox="0 0 24 24"><path d="M3.5 18 8.5 9.5l5.5 4.5 6.5-9"/></svg>',
    [MAP_DRAWING_MODES.POLYGON]: '<svg viewBox="0 0 24 24"><path d="M4 7.5 13 4l7.5 7.5-4.5 8.5-10.5-2.5Z"/></svg>',
    [MAP_DRAWING_MODES.RECTANGLE]: '<svg viewBox="0 0 24 24"><rect x="4" y="5" width="16" height="14"/></svg>',
    [MAP_DRAWING_MODES.CIRCLE]: '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="8"/></svg>',
};

const norm = mode => {
    const value = mode && mode !== "SearchBox" && mode.toString().toLowerCase();
    return Object.keys(LABELS).includes(value) ? value : null;
};

const compactPath = path => path.filter((p, i) => i === 0 || p.lat() !== path[i - 1].lat() || p.lng() !== path[i - 1].lng());
const callback = (props, name) => props[name] || props[name.charAt(0).toLowerCase() + name.slice(1)];
const POINT_HIT_PX = 12;

class GoogleMapsDrawingManager extends PureComponent {

    static contextTypes = {[MAP]: () => null};

    componentDidMount() {
        this.map = this.context[MAP];
        this.overlayView = new window.google.maps.OverlayView();
        this.overlayView.onAdd = () => {};
        this.overlayView.draw = () => {};
        this.overlayView.onRemove = () => {};
        this.overlayView.setMap(this.map);
        this.originalTouchAction = this.map.getDiv().style.touchAction;
        this.originalOptions = {
            disableDoubleClickZoom: this.map.get("disableDoubleClickZoom"),
            draggable: this.map.get("draggable"),
            draggableCursor: this.map.get("draggableCursor"),
            draggingCursor: this.map.get("draggingCursor"),
        };
        this.controlSignature = this.getControlSignature();
        this.addControl();
        this.setDrawingMode(this.props.drawingMode || this.props.defaultDrawingMode);
    }

    componentDidUpdate(prevProps) {
        const controlSignature = this.getControlSignature();
        if (controlSignature !== this.controlSignature) {
            this.removeControl();
            this.controlSignature = controlSignature;
            this.addControl();
        }
        if (prevProps.drawingMode !== this.props.drawingMode) {
            this.setDrawingMode(this.props.drawingMode);
        }
    }

    componentWillUnmount() {
        this.clearDraft();
        this.off();
        if (this.overlayView) this.overlayView.setMap(null);
        this.restoreMap();
        this.removeControl();
    }

    getOptions = () => ({...(this.props.defaultOptions || {}), ...(this.props.options || {})});
    getOverlayOptions = type => this.getOptions()[`${type}Options`] || {};
    getPreviewOptions = type => {
        const options = this.getOverlayOptions(type);
        return {...options, strokeOpacity: (options.strokeOpacity == null ? 1 : options.strokeOpacity) * 0.55};
    };
    getModes = () => ((this.getOptions().drawingControlOptions || {}).drawingModes || Object.keys(LABELS)).map(norm).filter(Boolean);
    getControlPosition = () => (this.getOptions().drawingControlOptions || {}).position || window.google.maps.ControlPosition.TOP_CENTER;
    getControlSignature = () => JSON.stringify({
        drawingControl: !!this.getOptions().drawingControl,
        position: this.getControlPosition(),
        modes: this.getModes(),
    });

    setDrawingMode = mode => {
        const nextMode = norm(mode);
        const previousMode = this.drawingMode;
        this.clearDraft();
        this.off();
        this.restoreMap();
        this.drawingMode = nextMode;
        this.updateControl();

        if (previousMode !== nextMode && this.props.onDrawingModeChange) {
            this.props.onDrawingModeChange(nextMode);
        }
        if (!nextMode) return;

        this.map.setOptions({
            disableDoubleClickZoom: true,
            draggable: ![MAP_DRAWING_MODES.RECTANGLE, MAP_DRAWING_MODES.CIRCLE].includes(nextMode),
            draggableCursor: "crosshair",
            draggingCursor: "crosshair",
        });
        this.map.getDiv().style.touchAction = "none";
        this.setCursor("crosshair");

        if (nextMode === MAP_DRAWING_MODES.MARKER) {
            this.on("pointerdown", this.startMarkerPointer);
            this.on("pointermove", this.moveMarkerPointer);
            this.on("pointerup", this.completeMarker);
            this.on("pointercancel", this.cancelMarkerPointer);
        }
        if ([MAP_DRAWING_MODES.POLYLINE, MAP_DRAWING_MODES.POLYGON].includes(nextMode)) {
            this.on("pointerdown", this.startPathPointer);
            this.on("pointerup", this.addPathPoint);
            this.on("pointercancel", this.cancelPathPointer);
            this.on("dblclick", this.completePath);
            this.on("contextmenu", this.completePath);
            this.on("pointermove", this.movePathPointer);
        }
        if ([MAP_DRAWING_MODES.RECTANGLE, MAP_DRAWING_MODES.CIRCLE].includes(nextMode)) {
            this.on("pointerdown", this.startDragShape);
            this.on("pointermove", this.previewDragShape);
            this.on("pointerup", this.completeDragShape);
            this.on("pointercancel", this.cancelDragShape);
        }
    };

    on = (eventName, handler) => {
        const listener = event => this.handleDomEvent(event, handler);
        this.map.getDiv().addEventListener(eventName, listener, true);
        this.listeners = [...(this.listeners || []), {eventName, listener}];
    };

    off = () => {
        (this.listeners || []).forEach(({eventName, listener}) => this.map.getDiv().removeEventListener(eventName, listener, true));
        this.listeners = [];
    };

    handleDomEvent = (domEvent, handler) => {
        const clickedDrawingControl = this.controlNode && this.controlNode.contains(domEvent.target);
        const clickedInteractiveElement = domEvent.target.closest(".gm-style-iw, .info-window, [role='button'], button, a, input, select, textarea");
        if (clickedDrawingControl || clickedInteractiveElement) return;
        if (domEvent.pointerType === "mouse" && ["pointerdown", "pointerup"].includes(domEvent.type) && domEvent.button !== 0) return;

        const latLng = this.getLatLng(domEvent);
        if (!latLng && domEvent.type !== "pointercancel") return;

        const isTapMode = [MAP_DRAWING_MODES.MARKER, MAP_DRAWING_MODES.POLYLINE, MAP_DRAWING_MODES.POLYGON].includes(this.drawingMode);
        const pointerMoved = this.drawingMode === MAP_DRAWING_MODES.MARKER ? this.markerPointerMoved : this.pathPointerMoved;
        const allowMapDrag = isTapMode && (domEvent.type === "pointerdown" || (domEvent.type === "pointerup" && pointerMoved));
        if (!allowMapDrag && (domEvent.type !== "pointermove" || this.draft || this.anchor)) {
            domEvent.preventDefault();
            domEvent.stopPropagation();
            domEvent.stopImmediatePropagation();
        }
        handler({latLng, domEvent});
    };

    getLatLng = event => {
        const projection = this.overlayView && this.overlayView.getProjection && this.overlayView.getProjection();
        if (!projection) return null;

        const rect = this.map.getDiv().getBoundingClientRect();
        return projection.fromContainerPixelToLatLng(new window.google.maps.Point(event.clientX - rect.left, event.clientY - rect.top));
    };

    restoreMap = () => {
        if (!this.map || !this.originalOptions) return;
        this.map.setOptions(this.originalOptions);
        this.map.getDiv().style.touchAction = this.originalTouchAction || "";
        this.setCursor("");
    };

    setCursor = cursor => {
        const div = this.map.getDiv();
        if (!document.getElementById("google-map-drawing-cursor")) {
            const style = document.createElement("style");
            style.id = "google-map-drawing-cursor";
            style.textContent = `
                .google-map-drawing-cursor .gm-style,
                .google-map-drawing-cursor .gm-style * {
                    cursor: var(--drawing-cursor) !important;
                }
                .google-map-drawing-cursor .gm-style-iw,
                .google-map-drawing-cursor .gm-style-iw * {
                    cursor: auto !important;
                }
                .google-map-drawing-cursor .gm-style-iw a,
                .google-map-drawing-cursor .gm-style-iw button {
                    cursor: pointer !important;
                }
            `;
            document.head.appendChild(style);
        }
        div.style.setProperty("--drawing-cursor", cursor || "auto");
        div.classList.toggle("google-map-drawing-cursor", !!cursor);
    };

    clearDraft = () => {
        if (this.draft) this.draft.setMap(null);
        if (this.previewSegment) this.previewSegment.setMap(null);
        if (this.startPoint) this.startPoint.setMap(null);
        this.anchor = null;
        this.draft = null;
        this.previewSegment = null;
        this.startPoint = null;
        this.path = [];
        this.cancelMarkerPointer();
        this.cancelPathPointer();
        this.shapeMoved = false;
    };

    finish = (name, overlay, type) => {
        const complete = callback(this.props, name);
        const overlayComplete = callback(this.props, "onOverlayComplete");
        if (complete) complete(overlay);
        if (overlayComplete) overlayComplete({overlay, type});
    };

    createMarker = position => {
        return new window.google.maps.Marker({map: this.map, position, ...this.getOverlayOptions("marker")});
    };

    completeMarker = event => {
        if (this.markerPointerId !== event.domEvent.pointerId || this.markerPointerMoved) {
            this.cancelMarkerPointer();
            return;
        }
        this.cancelMarkerPointer();
        this.finish("onMarkerComplete", this.createMarker(event.latLng), MAP_DRAWING_MODES.MARKER);
    };

    addPathPoint = event => {
        if (this.pathPointerId !== event.domEvent.pointerId || this.pathPointerMoved) {
            this.cancelPathPointer();
            return;
        }
        this.cancelPathPointer();
        if (event.domEvent && event.domEvent.detail > 1) return this.completePath(event);

        const isPolygon = this.drawingMode === MAP_DRAWING_MODES.POLYGON;
        const path = compactPath(this.path || []);
        const min = isPolygon ? 3 : 2;
        const first = path[0];
        const last = path[path.length - 1];

        if (!path.length) this.startPoint = new window.google.maps.Marker({map: this.map, position: event.latLng, icon: {fillColor: "#333", fillOpacity: 1, path: window.google.maps.SymbolPath.CIRCLE, scale: 5, strokeColor: "#fff", strokeWeight: 2}});

        if (path.length >= min && (this.isNear(event.latLng, last) || this.isNear(event.latLng, first))) {
            this.path = !isPolygon && this.isNear(event.latLng, first) && !this.isNear(event.latLng, last)
                ? [...path, first]
                : path;
            return this.completePath(event);
        }

        this.path = [...path, event.latLng];
        if (!this.draft) {
            this.draft = new window.google.maps.Polyline({clickable: false, map: this.map, path: this.path, ...this.getOverlayOptions(isPolygon ? "polygon" : "polyline")});
        } else {
            this.draft.setPath(this.path);
        }
    };

    startPathPointer = event => {
        this.startTapPointer("path", event.domEvent);
    };

    movePathPointer = event => {
        this.moveTapPointer("path", event.domEvent);
        this.previewPath(event);
    };

    cancelPathPointer = () => this.cancelTapPointer("path");

    startMarkerPointer = event => this.startTapPointer("marker", event.domEvent);

    moveMarkerPointer = event => this.moveTapPointer("marker", event.domEvent);

    cancelMarkerPointer = () => this.cancelTapPointer("marker");

    startTapPointer = (name, event) => {
        this.tapPointers = {...(this.tapPointers || {}), [event.pointerId]: true};
        this[`${name}PointerId`] = event.pointerId;
        this[`${name}PointerStart`] = {x: event.clientX, y: event.clientY};
        this[`${name}PointerMoved`] = Object.keys(this.tapPointers).length > 1;
    };

    moveTapPointer = (name, event) => {
        const start = this[`${name}PointerStart`];
        if (this[`${name}PointerId`] === event.pointerId && start) {
            this[`${name}PointerMoved`] = this[`${name}PointerMoved`] || Math.hypot(event.clientX - start.x, event.clientY - start.y) > POINT_HIT_PX;
        }
    };

    cancelTapPointer = name => {
        this.tapPointers = {};
        this[`${name}PointerId`] = null;
        this[`${name}PointerStart`] = null;
        this[`${name}PointerMoved`] = false;
    };

    previewPath = event => {
        if (event.domEvent.pointerType === "touch" || !this.draft || !this.path || !this.path.length) return;

        const path = compactPath(this.path);
        const min = this.drawingMode === MAP_DRAWING_MODES.POLYGON ? 3 : 2;
        const canFinish = path.length >= min && (this.isNear(event.latLng, path[0]) || this.isNear(event.latLng, path[path.length - 1]));
        this.setCursor(canFinish ? "pointer" : "crosshair");
        if (!this.previewSegment) {
            this.previewSegment = new window.google.maps.Polyline({clickable: false, map: this.map, path: [], ...this.getPreviewOptions(this.drawingMode === MAP_DRAWING_MODES.POLYGON ? "polygon" : "polyline")});
        }
        this.previewSegment.setPath([path[path.length - 1], event.latLng]);
    };

    completePath = event => {
        if (event.domEvent) {
            event.domEvent.preventDefault();
            event.domEvent.stopPropagation();
        }

        const isPolygon = this.drawingMode === MAP_DRAWING_MODES.POLYGON;
        const path = compactPath(this.path || []);
        if (!this.draft || path.length < (isPolygon ? 3 : 2)) return;

        if (this.startPoint) this.startPoint.setMap(null);
        this.startPoint = null;
        let overlay = this.draft;
        this.draft = null;
        if (this.previewSegment) this.previewSegment.setMap(null);
        this.previewSegment = null;
        this.path = [];
        this.setCursor("crosshair");
        overlay.setPath(path);
        if (isPolygon) {
            overlay.setMap(null);
            overlay = new window.google.maps.Polygon({map: this.map, path, ...this.getOverlayOptions("polygon")});
        }
        this.finish(isPolygon ? "onPolygonComplete" : "onPolylineComplete", overlay, isPolygon ? MAP_DRAWING_MODES.POLYGON : MAP_DRAWING_MODES.POLYLINE);
    };

    startDragShape = event => {
        if (this.draft && this.anchor) return this.completeDragShape(event, true);

        this.capturePointer(event);
        this.anchor = event.latLng;
        this.shapeMoved = false;
        this.draft = this.drawingMode === MAP_DRAWING_MODES.CIRCLE
            ? new window.google.maps.Circle({clickable: false, map: this.map, center: this.anchor, radius: 0, ...this.getOverlayOptions("circle")})
            : new window.google.maps.Rectangle({clickable: false, map: this.map, bounds: this.getBounds(this.anchor, this.anchor), ...this.getOverlayOptions("rectangle")});
    };

    previewDragShape = event => {
        if (!this.draft || !this.anchor) return;
        if (this.activePointerId != null && event.domEvent.pointerId !== this.activePointerId) return;
        if (!this.isNear(event.latLng, this.anchor)) this.shapeMoved = true;
        if (this.drawingMode === MAP_DRAWING_MODES.CIRCLE) this.draft.setRadius(this.getDistance(this.anchor, event.latLng));
        if (this.drawingMode === MAP_DRAWING_MODES.RECTANGLE) this.draft.setBounds(this.getBounds(this.anchor, event.latLng));
    };

    completeDragShape = (event, force = false) => {
        if (!this.draft || !this.anchor) return;
        if (this.activePointerId != null && event.domEvent.pointerId !== this.activePointerId) return;
        this.previewDragShape(event);
        this.releasePointer(event);
        if (!force && !this.shapeMoved) return;

        const type = this.drawingMode;
        const overlay = this.draft;
        this.anchor = null;
        this.draft = null;
        this.shapeMoved = false;
        if (type === MAP_DRAWING_MODES.CIRCLE) {
            overlay.center = overlay.getCenter();
            overlay.radius = overlay.getRadius();
        }
        this.finish(type === MAP_DRAWING_MODES.CIRCLE ? "onCircleComplete" : "onRectangleComplete", overlay, type);
    };

    cancelDragShape = event => {
        this.releasePointer(event);
        this.clearDraft();
    };

    capturePointer = event => {
        const domEvent = event.domEvent;
        if (!domEvent || domEvent.pointerId == null || !domEvent.currentTarget.setPointerCapture) return;
        this.activePointerId = domEvent.pointerId;
        domEvent.currentTarget.setPointerCapture(domEvent.pointerId);
    };

    releasePointer = event => {
        const domEvent = event.domEvent;
        const target = domEvent && domEvent.currentTarget;
        if (target && this.activePointerId != null && target.releasePointerCapture && (!target.hasPointerCapture || target.hasPointerCapture(this.activePointerId))) {
            target.releasePointerCapture(this.activePointerId);
        }
        this.activePointerId = null;
    };

    getBounds = (a, b) => {
        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend(a);
        bounds.extend(b);
        return bounds;
    };

    getDistance = (a, b) => window.google.maps.geometry.spherical.computeDistanceBetween(a, b);

    isNear = (a, b) => {
        const first = this.getPoint(a);
        const second = this.getPoint(b);
        return !!first && !!second && Math.hypot(first.x - second.x, first.y - second.y) <= POINT_HIT_PX;
    };

    getPoint = latLng => {
        const projection = latLng && this.overlayView && this.overlayView.getProjection && this.overlayView.getProjection();
        if (!projection) return null;
        return projection.fromLatLngToContainerPixel(latLng);
    };

    addControl = () => {
        if (!this.getOptions().drawingControl) return;

        this.controlNode = document.createElement("div");
        Object.assign(this.controlNode.style, {
            background: "#fff",
            borderRadius: "2px",
            boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
            display: "flex",
            fontFamily: "Roboto, Arial, sans-serif",
            margin: "6px 10px",
            overflow: "hidden",
        });

        this.controlNode.appendChild(this.createButton(null, ICONS.pan, "Stop drawing"));
        this.getModes().forEach(mode => this.controlNode.appendChild(this.createButton(mode, ICONS[mode], LABELS[mode])));
        this.controlPosition = this.getControlPosition();
        this.map.controls[this.controlPosition].push(this.controlNode);
        this.updateControl();
    };

    removeControl = () => {
        if (!this.controlNode) return;
        const controls = this.map && this.controlPosition && this.map.controls[this.controlPosition];
        if (controls) {
            for (let i = controls.getLength() - 1; i >= 0; i -= 1) {
                if (controls.getAt(i) === this.controlNode) controls.removeAt(i);
            }
        } else if (this.controlNode.parentNode) {
            this.controlNode.parentNode.removeChild(this.controlNode);
        }
        this.controlNode = null;
        this.controlPosition = null;
    };

    createButton = (mode, icon, title) => {
        const button = document.createElement("button");
        button.innerHTML = icon;
        button.title = title;
        button.type = "button";
        button.setAttribute("data-drawing-mode", mode || "");
        Object.assign(button.style, {
            alignItems: "center",
            background: "#fff",
            border: "0",
            borderRight: "1px solid #ddd",
            color: "#333",
            cursor: "pointer",
            display: "flex",
            height: "28px",
            justifyContent: "center",
            padding: "0",
            width: "28px",
        });
        button.onclick = event => {
            event.preventDefault();
            event.stopPropagation();
            this.setDrawingMode(mode);
        };

        const svg = button.querySelector("svg");
        Object.assign(svg.style, {
            fill: "none",
            height: "18px",
            stroke: "currentColor",
            strokeLinecap: "round",
            strokeLinejoin: "round",
            strokeWidth: "1.8",
            width: "18px",
        });
        return button;
    };

    updateControl = () => {
        if (!this.controlNode) return;
        Array.from(this.controlNode.querySelectorAll("button")).forEach(button => {
            const active = norm(button.getAttribute("data-drawing-mode")) === this.drawingMode || (!this.drawingMode && !button.getAttribute("data-drawing-mode"));
            button.style.background = active ? "#e6f0ff" : "#fff";
            button.style.color = active ? "#174ea6" : "#333";
        });
    };

    render() { return false; }
}

export default GoogleMapsDrawingManager;
